import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, BookOpen, Loader2 } from "lucide-react";

export default function CaseExamplesPage() {
  const [examples, setExamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");

  useEffect(() => {
    api.get("/content/examples").then(r => setExamples(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = category === "all" ? examples : examples.filter(e => e.category === category);
  const categories = ["all", ...new Set(examples.map(e => e.category))];
  const catLabels = { all: "Alle", alkohol: "Alkohol", drogen: "Drogen", punkte: "Punkte", straftaten: "Straftaten" };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (!loading && examples.length === 0) {
    return (
      <div className="page-transition space-y-6" data-testid="case-examples-page">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Fallbeispiele</h1>
          <p className="font-body text-sm text-muted-foreground mt-1">Lernen Sie von anonymisierten Beispielen - gute vs. problematische Antworten</p>
        </div>
        <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="examples-empty-state">
          <BookOpen className="w-12 h-12 text-muted-foreground/40 mb-4" />
          <h2 className="font-heading text-lg font-semibold mb-2">Noch keine Fallbeispiele verfügbar</h2>
          <p className="font-body text-sm text-muted-foreground max-w-md">
            Fallbeispiele werden in Kürze hinzugefügt. In der Zwischenzeit können Sie mit dem Lernpfad starten oder ein Übungsgespräch beginnen.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-transition space-y-6" data-testid="case-examples-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Fallbeispiele</h1>
        <p className="font-body text-sm text-muted-foreground mt-1">Lernen Sie von anonymisierten Beispielen - gute vs. problematische Antworten</p>
      </div>

      <div className="bg-muted/50 rounded-xl p-4 flex items-start gap-3">
        <BookOpen className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <p className="font-body text-xs text-muted-foreground">
          Diese Beispiele basieren auf typischen MPU-Szenarien und sind anonymisiert. Sie dienen als Orientierung,
          um zu verstehen, worauf Gutachter achten und wie reflektierte Antworten klingen.
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {categories.map(c => (
          <Badge key={c} variant={category === c ? "default" : "secondary"} className="cursor-pointer font-body text-xs"
            onClick={() => setCategory(c)} data-testid={`cat-${c}`}>
            {catLabels[c] || c}
          </Badge>
        ))}
      </div>

      <div className="space-y-6">
        {filtered.map((ex, i) => (
          <Card key={i} className="border-border/50" data-testid={`example-${ex.id}`}>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-body text-xs">{catLabels[ex.category] || ex.category}</Badge>
                <CardTitle className="font-heading text-lg">{ex.title}</CardTitle>
              </div>
              <CardDescription className="font-body text-sm">{ex.scenario}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Good Answer */}
              <div className="rounded-xl border-l-4 border-l-success bg-success/5 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-success" />
                  <p className="font-body text-sm font-semibold text-success">Gute Antwort</p>
                </div>
                <p className="font-body text-sm leading-relaxed italic">"{ex.good_answer}"</p>
              </div>
              {/* Bad Answer */}
              <div className="rounded-xl border-l-4 border-l-error bg-error/5 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="w-4 h-4 text-error" />
                  <p className="font-body text-sm font-semibold text-error">Problematische Antwort</p>
                </div>
                <p className="font-body text-sm leading-relaxed italic">"{ex.bad_answer}"</p>
              </div>
              {/* Analysis */}
              <div className="bg-muted/30 rounded-xl p-4">
                <p className="font-body text-xs font-medium text-primary mb-1">Analyse:</p>
                <p className="font-body text-sm text-muted-foreground leading-relaxed">{ex.analysis}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
